import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Crown } from "lucide-react";
import { api } from "@/lib/api";
import { effectivePrice, isOnSale } from "@/lib/pricing";
import { ParticleField } from "@/components/ParticleField";
import { UsernameDialog } from "@/components/UsernameDialog";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const FAQS = [
  {
    q: "How do I cancel my subscription?",
    a: "Anytime, no hoops. Open the receipt email Tebex sent you when you subscribed and click the manage/cancel link — or enter your email on the checkout's payment lookup page to pull up every payment and cancel from there. Your rank stays active until the end of the month you already paid for.",
  },
  {
    q: "When do I get my rank?",
    a: "Instantly. The moment your payment clears, our Tebex plugin delivers the rank in-game — whether you're online right then or the next time you join.",
  },
  {
    q: "What happens if I cancel mid-month?",
    a: "Nothing bad. You keep every perk until your current month runs out, and you're never charged again. Resubscribe whenever you want — same price, same perks.",
  },
];

export default function UltiPlusPage() {
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    api.get("/packages", { params: { category: "ranks" } })
      .then((r) => setPkg(r.data.find((p) => p.name.includes("+")) || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const descriptionLines = pkg?.description ? pkg.description.split("\n").filter(Boolean) : [];
  const hasBullets = !pkg?.perks?.length && descriptionLines.length > 1;
  const intro = hasBullets ? descriptionLines[0] : pkg?.description;
  const bullets = pkg?.perks?.length ? pkg.perks : hasBullets ? descriptionLines.slice(1) : [];

  return (
    <main data-testid="ulti-plus-page" className="relative min-h-screen bg-[#05030A] overflow-hidden">
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 55% 45% at 65% 40%, rgba(139,92,246,0.18), transparent 70%)" }} />
      <ParticleField className="absolute inset-0" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#05030A]" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pt-36 pb-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center min-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.35em] text-[#A78BFA]" data-testid="ulti-plus-overline">
            <Crown className="w-4 h-4" /> THE TOP RANK
          </p>
          <h1 className="mt-5 font-display text-6xl sm:text-7xl lg:text-8xl tracking-tight text-white" data-testid="ulti-plus-title">
            ULTI<span className="text-glow bg-gradient-to-br from-[#EDE9FE] via-[#A78BFA] to-[#7C3AED] bg-clip-text text-transparent">+</span>
          </h1>
          <p className="mt-6 text-slate-400 text-base sm:text-lg leading-relaxed max-w-lg" data-testid="ulti-plus-description">
            {intro || "The highest rank on ULTISMP. Every perk, every command, every key — the full realm, unlocked."}
          </p>

          {bullets.length > 0 && (
            <ul className="mt-6 space-y-2.5">
              {bullets.map((perk) => (
                <li key={perk} className="flex items-start gap-2.5 text-sm text-slate-300">
                  <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#8B5CF6]" strokeWidth={3} />
                  {perk}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-10 flex flex-wrap items-center gap-5">
            {pkg && (
              <div className="flex items-baseline gap-3">
                {isOnSale(pkg) && (
                  <span className="text-slate-500 line-through text-lg">${pkg.price.toFixed(2)}</span>
                )}
                <span className="font-display text-5xl text-white" data-testid="ulti-plus-price">
                  ${effectivePrice(pkg).toFixed(2)}
                </span>
                {pkg.recurring ? (
                  <span className="text-xs tracking-[0.2em] text-slate-500">
                    /{pkg.recurring.toUpperCase()}
                    <span className="block mt-1 text-[10px] tracking-normal text-slate-600 normal-case" data-testid="ulti-plus-cancel-note">Subscription · cancel anytime</span>
                  </span>
                ) : (
                  <span className="text-xs tracking-[0.2em] text-slate-500">ONE-TIME</span>
                )}
              </div>
            )}
            <button
              onClick={() => pkg && setPending(pkg)}
              disabled={!pkg}
              data-testid="ulti-plus-purchase-button"
              className="group inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] disabled:opacity-50 text-white text-sm font-bold tracking-[0.15em] shadow-[0_0_40px_rgba(139,92,246,0.45)] hover:shadow-[0_0_60px_rgba(139,92,246,0.65)] transition-[background-color,box-shadow,transform] duration-300 hover:-translate-y-0.5"
            >
              PURCHASE ULTI+
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>

          <Link
            to="/store/ranks"
            data-testid="ulti-plus-compare-link"
            className="mt-8 inline-block text-xs font-bold tracking-[0.2em] text-slate-500 hover:text-[#A78BFA] transition-colors duration-300"
          >
            COMPARE WITH ULTI RANK →
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <motion.div
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="relative rounded-3xl overflow-hidden border border-[#8B5CF6]/30 shadow-[0_0_80px_rgba(139,92,246,0.3)]"
          >
            {pkg?.image_url ? (
              <img src={pkg.image_url} alt="ULTI+ Rank" className="w-full aspect-[4/3] object-cover" data-testid="ulti-plus-image" />
            ) : (
              <div className="w-full aspect-[4/3] bg-[#0D0B12] flex items-center justify-center">
                <Crown className="w-20 h-20 text-[#8B5CF6]/40" strokeWidth={1} />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#05030A]/80 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5">
              <p className="text-[10px] font-bold tracking-[0.3em] text-[#A78BFA]">ULTISMP RANK</p>
              <p className="font-display text-3xl text-white mt-1">ULTI+</p>
            </div>
          </motion.div>
          <div className="absolute -inset-8 -z-10 rounded-full blur-3xl opacity-30" style={{ background: "radial-gradient(circle, #8B5CF6, transparent 65%)" }} />
        </motion.div>
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          data-testid="ulti-plus-faq"
        >
          <p className="text-[11px] font-bold tracking-[0.35em] text-[#A78BFA]">QUESTIONS</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight text-white">No lock-ins, ever</h2>
          <Accordion type="single" collapsible className="mt-6">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="border-white/10">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-sm font-semibold text-white hover:text-[#A78BFA] hover:no-underline transition-colors duration-300">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent data-testid={`faq-content-${i}`} className="text-sm text-slate-400 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>

      <UsernameDialog pkg={pending} onClose={() => setPending(null)} />
    </main>
  );
}
